import { ApolloError } from "@apollo/client/core";
import { AbortReason } from "@/core/api/common/enums";
import { graphqlClient } from "@/core/api/graphql/client";
// Innovadis custom: Import the actual query document directly to avoid dependency on regenerated types for this change.
import GetSearchResultsDocument from "./getSearchResultsQuery.graphql";
import type { GetSearchResultsQuery, GetSearchResultsQueryVariables } from "@/core/api/graphql/types"; // Result type remains valid; variables changed only
import { useModuleSettings } from "@/core/composables/useModuleSettings";
import { DEFAULT_PAGE_SIZE } from "@/core/constants";
import { MODULE_ID_VIRTOPAGES, VIRTOPAGES_ENABLED_KEY } from "@/core/constants/modules";
import { globals } from "@/core/globals";
import { Logger } from "@/core/utilities/logger";

export type GetSearchResultsParamsType = {
  keyword: string;
  // Innovadis custom: filter is DEPRECATED!
  // Use articleGroupsFilter/variationsFilter/categoriesFilter below. Kept filter available for backward compatibility.
  // If provided, it will be applied to articleGroupsFilter by default.
  filter?: string;

  // Innovadis custom: separate filters so categories aren't constrained by product-specific filters.
  articleGroupsFilter?: string;
  variationsFilter?: string;
  categoriesFilter?: string;

  productSuggestions?: {
    suggestionsSize?: number;
  };

  pages?: {
    page?: number;
    itemsPerPage?: number;
  };

  categories?: {
    page?: number;
    itemsPerPage?: number;
    sort?: string;
    fuzzy?: boolean;
    fuzzyLevel?: number;
  };

  articleGroups?: {
    page?: number;
    itemsPerPage?: number;
    sort?: string;
    fuzzy?: boolean;
    fuzzyLevel?: number;
  };

  variations?: {
    page?: number;
    itemsPerPage?: number;
    sort?: string;
    fuzzy?: boolean;
    fuzzyLevel?: number;
  };
};

let abortController: AbortController | undefined;

const { isEnabled: virtoPagesEnabled } = useModuleSettings(MODULE_ID_VIRTOPAGES);

export async function getSearchResults(params: GetSearchResultsParamsType) {
  const { storeId, userId, cultureName, currencyCode } = globals;

  const isVirtoPagesEnabled = virtoPagesEnabled(VIRTOPAGES_ENABLED_KEY);

  const withSuggestions = !!params.productSuggestions;
  const withPages = !!params.pages && !isVirtoPagesEnabled;
  const withVirtoPages = !!params.pages && isVirtoPagesEnabled;
  const withCategories = !!params.categories;
  // Innovadis custom: allow article groups and variations to be requested as separate result sets.
  const withArticleGroups = !!params.articleGroups;
  const withVariations = !!params.variations;

  const {
    keyword,
    // Innovadis custom: Prefer explicit split filters; fall back to legacy filter for article groups.
    filter, // legacy
    articleGroupsFilter,
    variationsFilter,
    categoriesFilter,

    productSuggestions: { suggestionsSize: productSuggestionsSize = DEFAULT_PAGE_SIZE } = {},

    pages: { page: staticContentPage = 1, itemsPerPage: staticContentItemsPerPage = DEFAULT_PAGE_SIZE } = {},

    categories: {
      itemsPerPage: categoriesItemsPerPage = DEFAULT_PAGE_SIZE,
      page: categoriesPage = 1,
      sort: categoriesSort,
      fuzzy: categoriesFuzzy,
      fuzzyLevel: categoriesFuzzyLevel,
    } = {},

    articleGroups: {
      itemsPerPage: articleGroupsItemsPerPage = DEFAULT_PAGE_SIZE,
      page: articleGroupsPage = 1,
      sort: articleGroupsSort,
      fuzzy: articleGroupsFuzzy,
      fuzzyLevel: articleGroupsFuzzyLevel,
    } = {},

    variations: {
      itemsPerPage: variationsItemsPerPage = DEFAULT_PAGE_SIZE,
      page: variationsPage = 1,
      sort: variationsSort,
      fuzzy: variationsFuzzy,
      fuzzyLevel: variationsFuzzyLevel,
    } = {},
  } = params;

  // Innovadis custom: use a loose type for variables to avoid mismatch until codegen is run.
  const variables: Record<string, unknown> = {
    storeId,
    userId,
    cultureName,
    currencyCode,
    // Innovadis custom: the legacy top-level `products` field is superseded by the
    // articleGroups/variations aliases below and its result is unused; always keep it disabled.
    withProducts: false,
    withArticleGroups,
    withVariations,
    withCategories,
    withPages,
    withVirtoPages,
    withSuggestions,
    query: keyword,
    // Apply dedicated filters. If only legacy filter is provided, use it for article groups only.
    articleGroupsFilter: articleGroupsFilter ?? filter,
    variationsFilter,
    categoriesFilter,
  };

  if (withSuggestions) {
    Object.assign(variables, {
      suggestionsSize: productSuggestionsSize,
    });
  }

  if (withPages || withVirtoPages) {
    Object.assign(variables, <GetSearchResultsQueryVariables>{
      pagesFirst: staticContentItemsPerPage,
      pagesAfter: String((staticContentPage - 1) * staticContentItemsPerPage),
    });
  }

  if (withCategories) {
    Object.assign(variables, <GetSearchResultsQueryVariables>{
      categoriesFilter,
      categoriesSort,
      categoriesFuzzy,
      categoriesFuzzyLevel,
      categoriesFirst: categoriesItemsPerPage,
      categoriesAfter: String((categoriesPage - 1) * categoriesItemsPerPage),
    });
  }

  if (withArticleGroups) {
    // Innovadis custom: use dedicated pagination and sorting for the article groups section.
    Object.assign(variables, {
      articleGroupsSort,
      articleGroupsFuzzy,
      articleGroupsFuzzyLevel,
      articleGroupsFirst: articleGroupsItemsPerPage,
      articleGroupsAfter: String((articleGroupsPage - 1) * articleGroupsItemsPerPage),
    });
  }

  if (withVariations) {
    // Innovadis custom: use dedicated pagination and sorting for the variations section.
    Object.assign(variables, {
      variationsSort,
      variationsFuzzy,
      variationsFuzzyLevel,
      variationsFirst: variationsItemsPerPage,
      variationsAfter: String((variationsPage - 1) * variationsItemsPerPage),
    });
  }

  if (abortController) {
    abortController.abort(AbortReason.Explicit);
  }

  abortController = new AbortController();
  const { signal } = abortController;

  try {
    // Type the query result to avoid implicit 'any' on destructuring.
    const { data } = await graphqlClient.query<GetSearchResultsQuery>({
      query: GetSearchResultsDocument,
      variables,
      context: {
        fetchOptions: { signal },
      },
    });

    return {
      productSuggestions: data.productSuggestions ?? {},
      pages: data.pages ?? data.pageDocuments ?? {},
      categories: data.categories ?? {},
      // Innovadis custom: expose aliased product queries without waiting for regenerated types.
      articleGroups: (data as GetSearchResultsQuery & { articleGroups?: unknown }).articleGroups ?? {},
      variations: (data as GetSearchResultsQuery & { variations?: unknown }).variations ?? {},
    };
  } catch (e) {
    if (e instanceof ApolloError && e.networkError?.toString() === (AbortReason.Explicit as string)) {
      return;
    }
    Logger.error(`${getSearchResults.name}`, e);
    throw e;
  }
}
